import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Field, PageHeader, StatusBadge } from "@/components/StatusBits";
import { Transcript } from "@/components/dialer/ContactPanel";
import { useApp } from "@/context/AppContext";
import { api, errMsg, fmtDT, fmtDur, label } from "@/lib/api";

const Detail = ({ id, onClose }) => {
  const [c, setC] = useState(null);
  useEffect(() => {
    if (id) api.get(`/calls/${id}`).then((r) => setC(r.data)).catch(() => {});
  }, [id]);
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-white w-full sm:max-w-lg overflow-y-auto" data-testid="call-detail">
        <SheetHeader><SheetTitle>{c?.contact_name || "Call"}</SheetTitle></SheetHeader>
        {c && (
          <div className="space-y-3 text-sm mt-4">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <p><b>Direction:</b> {c.direction}</p><p><b>Mode:</b> {label(c.mode)}</p>
              <p><b>To:</b> {c.to_number}</p><p><b>Caller ID:</b> {c.from_number}</p>
              <p><b>Agent:</b> {c.agent_name || "—"}</p><p><b>Outcome:</b> {label(c.outcome)}</p>
              <p><b>Started:</b> {fmtDT(c.created_at)}</p><p><b>Answered:</b> {fmtDT(c.answered_at)}</p>
              <p><b>Ended:</b> {fmtDT(c.ended_at)}</p><p><b>Duration:</b> {fmtDur(c.duration_seconds)}</p>
              <p className="col-span-2 font-mono text-[10px]"><b>Provider call ID:</b> {c.twilio_sid || "—"}</p>
              {c.script_name && <p className="col-span-2"><b>Script:</b> {c.script_name}</p>}
            </div>
            {c.summary && <p className="text-xs"><b>AI summary:</b> {c.summary}</p>}
            {c.next_action && <p className="text-xs"><b>Next action:</b> {c.next_action}</p>}
            {c.notes && <p className="text-xs"><b>Notes:</b> {c.notes}</p>}
            {c.transcript?.length > 0 && <Transcript items={c.transcript} />}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default function History() {
  const { isAdmin } = useApp();
  const [f, setF] = useState({ date_from: "", date_to: "", q: "", agent_id: "", outcome: "" });
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [open, setOpen] = useState(null);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const load = useCallback(() => api.get("/calls", { params }).then((r) => setRows(r.data)).catch(() => {}), [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => {
    if (isAdmin) api.get("/users").then((r) => setAgents(r.data.users.filter((u) => u.role !== "pending"))).catch(() => {});
  }, [isAdmin]);
  const exportCsv = async () => {
    try {
      const res = await api.get("/calls/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "phone-blower-calls.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e === "all" ? "" : e });
  return (
    <div className="max-w-7xl p-6 space-y-5">
      <PageHeader title="Call history" sub="Every call with caller ID, agent, provider ID, timing, outcome, notes and AI transcript.">
        <Button variant="outline" data-testid="history-export-btn" onClick={exportCsv}><Download className="h-4 w-4 mr-1" />Export CSV</Button>
      </PageHeader>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Field label="From"><Input type="date" data-testid="history-date-from" value={f.date_from} onChange={set("date_from")} className="bg-white" /></Field>
        <Field label="To"><Input type="date" data-testid="history-date-to" value={f.date_to} onChange={set("date_to")} className="bg-white" /></Field>
        <Field label="Contact"><Input data-testid="history-contact-search" value={f.q} onChange={set("q")} placeholder="Name" className="bg-white" /></Field>
        {isAdmin && (
          <Field label="Agent">
            <Select value={f.agent_id || "all"} onValueChange={set("agent_id")}>
              <SelectTrigger data-testid="history-agent-filter" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="all">All agents</SelectItem>{agents.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name || a.email}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        )}
        <Field label="Outcome">
          <Select value={f.outcome || "all"} onValueChange={set("outcome")}>
            <SelectTrigger data-testid="history-outcome-filter" className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All outcomes</SelectItem>
              {["conversation", "callback_requested", "resolved", "not_interested", "no_answer", "busy", "voicemail", "missed", "opted_out", "do_not_call", "wrong_number", "failed", "canceled"].map((o) => <SelectItem key={o} value={o}>{label(o)}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>When</TableHead><TableHead>Contact</TableHead><TableHead>Direction</TableHead><TableHead>Mode</TableHead>
            <TableHead>Caller ID</TableHead><TableHead>Agent</TableHead><TableHead>Duration</TableHead><TableHead>Outcome</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} className="text-sm cursor-pointer" data-testid={`history-row-${c.id}`} onClick={() => setOpen(c.id)}>
                <TableCell className="py-2 text-xs">{fmtDT(c.created_at)}</TableCell>
                <TableCell className="py-2 font-medium">{c.contact_name || c.from_number}</TableCell>
                <TableCell className="py-2 text-xs">{c.direction}</TableCell>
                <TableCell className="py-2 text-xs">{label(c.mode)}</TableCell>
                <TableCell className="py-2 font-mono text-xs">{c.direction === "inbound" ? c.to_number : c.from_number}</TableCell>
                <TableCell className="py-2 text-xs">{c.agent_name || "—"}</TableCell>
                <TableCell className="py-2 text-xs">{fmtDur(c.duration_seconds)}</TableCell>
                <TableCell className="py-2"><StatusBadge value={c.outcome || c.status} /></TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-500 py-8" data-testid="history-empty">No calls match these filters.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Detail id={open} onClose={() => setOpen(null)} />
    </div>
  );
}
